import {
	getFormProps,
	getInputProps,
	getSelectProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import {
	Form,
	json,
	Link,
	Outlet,
	useLoaderData,
	useSearchParams,
	useSubmit,
} from '@remix-run/react'
import { useRef, useState } from 'react'
import slugify from 'slugify'
import { z } from 'zod'
import Main from '#app/components/main.js'
import Sidebar from '#app/components/sidebar.js'
import { Button } from '#app/components/ui/button.js'
import { Icon } from '#app/components/ui/icon.js'
import { Input } from '#app/components/ui/input.js'
import MySelect, { type Options } from '#app/components/ui/multiple-select.js'
import { Separator } from '#app/components/ui/separator.tsx'
import { StatusButton } from '#app/components/ui/status-button.js'
import { prisma } from '#app/utils/db.server.js'
import { useDebounce, useIsPending } from '#app/utils/misc.js'
import { useOptionalUser } from '#app/utils/user.js'

const SearchAndFilterSchema = z.object({
	search: z.string().optional(),
	sortBy: z.enum(['all', 'videos', 'photos']).optional(),
	contributors: z.array(z.string()),
	wildlife: z.array(z.string()),
	ecosystems: z.array(z.string()),
	perspectives: z.array(z.string()),
	categories: z.array(z.string()),
	works: z.string().optional(),
})

const workOptions = [
	{ label: 'Photos and Videos', value: 'all' },
	{ label: 'Videos', value: 'videos' },
	{ label: 'Photos', value: 'photos' },
]

const wildlifeOptions = [
	{ label: 'Birds', value: slugify('Birds').toLocaleLowerCase() },
	{ label: 'Coral', value: slugify('Coral').toLocaleLowerCase() },
	{ label: 'Crustacean', value: slugify('Crustacean').toLocaleLowerCase() },
	{ label: 'Fish', value: slugify('Fish').toLocaleLowerCase() },
	{ label: 'Jellyfish', value: slugify('Jellyfish').toLocaleLowerCase() },
	{
		label: 'Seals & Pinnipeds',
		value: slugify('Seals & Pinnipeds').toLocaleLowerCase(),
	},
	{
		label: 'Sharks and Rays',
		value: slugify('Sharks and Rays').toLocaleLowerCase(),
	},
	{ label: 'Turtles', value: slugify('Turtles').toLocaleLowerCase() },
	{
		label: 'Whales & Dolphins',
		value: slugify('Whales & Dolphins').toLocaleLowerCase(),
	},
]
const categoryOptions = [
	{
		label: 'Conservation',
		value: slugify('Conservation').toLocaleLowerCase(),
	},
	{ label: 'Industry', value: slugify('Industry').toLocaleLowerCase() },
	{ label: 'People', value: slugify('People').toLocaleLowerCase() },
	{ label: 'Scenics', value: slugify('Scenics').toLocaleLowerCase() },
	{ label: 'Science', value: slugify('Science').toLocaleLowerCase() },
	{ label: 'Wildlife', value: slugify('Wildlife').toLocaleLowerCase() },
]
const ecosystemOptions = [
	{
		label: 'Arctic and Antarctic',
		value: slugify('Arctic and Antarctic').toLocaleLowerCase(),
	},
	{ label: 'Cave', value: slugify('Cave').toLocaleLowerCase() },
	{ label: 'Coastal', value: slugify('Coastal').toLocaleLowerCase() },
	{ label: 'Coral Reef', value: slugify('Coral Reef').toLocaleLowerCase() },
	{ label: 'Deep Sea', value: slugify('Deep Sea').toLocaleLowerCase() },
	{ label: 'Desert', value: slugify('Desert').toLocaleLowerCase() },
	{ label: 'Freshwater', value: slugify('Freshwater').toLocaleLowerCase() },
	{ label: 'Kelp', value: slugify('Kelp').toLocaleLowerCase() },
	{ label: 'Ocean', value: slugify('Ocean').toLocaleLowerCase() },
	{ label: 'Sea Grass', value: slugify('Sea Grass').toLocaleLowerCase() },
	{ label: 'Shipwreck', value: slugify('Shipwreck').toLocaleLowerCase() },
	{ label: 'Tropical', value: slugify('Tropical').toLocaleLowerCase() },
	{ label: 'Urban', value: slugify('Urban').toLocaleLowerCase() },
]
const perspectiveOptions = [
	{ label: 'Above Water', value: slugify('Above Water').toLocaleLowerCase() },
	{
		label: 'Drone and Aerial',
		value: slugify('Drone and Aerial').toLocaleLowerCase(),
	},
	{ label: 'Underwater', value: slugify('Underwater').toLocaleLowerCase() },
]
const autoSubmit = true

export const loader = async () => {
	const artists = await prisma.user.findMany({
		select: {
			name: true,
			username: true,
		},
		where: {
			works: {
				some: {},
			},
			roles: {
				some: {
					name: {
						in: ['image', 'video'],
					},
				},
			},
		},
		orderBy: {
			name: 'asc',
		},
	})
	return json({ artists })
}

export default function AppLayout() {
	const user = useOptionalUser()
	const { artists } = useLoaderData<typeof loader>()
	const artistOptions: Options = artists.map(a => ({
		label: a.name || a.username,
		value: a.username,
	}))
	const [searchParams] = useSearchParams()
	const [page, setPage] = useState<string>(searchParams.get('page') ?? '1')
	const submit = useSubmit()
	// const filtersOpen = !!(
	// 	searchParams.get('contributors') ||
	// 	searchParams.get('ecosystems') ||
	// 	searchParams.get('categories') ||
	// 	searchParams.get('wildlife') ||
	// 	searchParams.get('perspectives')
	// )
	const [showFilters, setShowFilters] = useState<boolean>(true)
	const isSubmitting = useIsPending({
		formMethod: 'GET',
		formAction: '/',
	})
	const formRef = useRef<HTMLFormElement>(null)
	const [form, fields] = useForm({
		id: 'search-and-filter-form',
		constraint: getZodConstraint(SearchAndFilterSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: SearchAndFilterSchema })
		},
		shouldRevalidate: 'onBlur',
	})
	const handleFormChange = useDebounce((formElement: HTMLFormElement) => {
		if (form.valid) {
			setPage('1')
			submit(formElement)
		}
	}, 500)
	const handleSelectChange = () => {
		form.validate()
		return formRef.current && handleFormChange(formRef.current)
	}
	return (
		<>
			{!user && (
				<>
					<div className="banner min-h-[70vh] w-full px-4 pt-12 md:pt-28 lg:px-24">
						<div className="flex w-full flex-wrap items-center justify-between gap-4">
							<h2 className="font-serif text-3xl font-black md:text-5xl xl:text-7xl">
								The Oceanic Library
							</h2>
							<Link to="/login" className="font-serif text-xs tracking-wide">
								Login
								<Icon className="ml-1" name="arrow-right" />
							</Link>
						</div>
						<Separator className="mt-4 lg:mt-8" />
						<div className="flex w-full items-center justify-end font-mono uppercase">
							<Button
								variant={'outline'}
								asChild
								className="w-1/2 rounded-none border-b-0 border-r-0 border-t-0 lg:w-auto"
							>
								<Link to="/apply" className="tracking-wide">
									Apply for Membership
								</Link>
							</Button>
							<Button
								variant={'outline'}
								asChild
								className="w-1/2 rounded-none border-b-0 border-t-0 lg:w-auto"
							>
								<Link to="/contributor/apply" className="tracking-wide">
									Become a Contributor
								</Link>
							</Button>
						</div>
						<div className="relative">
							<img
								src="/backgrounds/firework-2160.jpeg"
								alt="Firework"
								className="h-full w-full"
							/>
							<img
								src="/img/un.png"
								alt="UN Endorsement"
								className="absolute bottom-4 left-4 h-auto w-[12%]"
							/>
						</div>
						<div className="flex w-full flex-wrap justify-between gap-4 py-4 md:gap-8 md:py-8 lg:gap-16">
							<div className="flex flex-1 basis-full md:w-[50ch] md:basis-auto">
								<h2 className="font-serif text-xl font-black lg:text-2xl">
									A curated library of photos and videos of the marine world,
									free for conservation
								</h2>
							</div>
							<div className="flex flex-1 basis-full justify-end md:basis-[45ch]">
								<p className="inline w-full text-balance text-base md:text-right">
									We serve those who are doing the hard on the ground. Library
									members include small-scale organizations, educational
									institutions, scientists, large multi-national NGOs and
									governmental agencies.
								</p>
							</div>
						</div>
					</div>
					<Separator className="" />
				</>
			)}
			<Sidebar>
				<Form
					method="GET"
					ref={formRef}
					action="/"
					{...getFormProps(form)}
					className="flex flex-wrap items-center justify-center gap-2 font-mono"
					onChange={e => autoSubmit && handleFormChange(e.currentTarget)}
				>
					<input type="hidden" name="page" value={page} />
					<MySelect
						{...getSelectProps(fields.works)}
						name="works"
						isMulti={false}
						isSearchable={false}
						onChange={handleSelectChange}
						className="bg-white text-[0.8rem]"
						defaultOptions={
							searchParams.get('works')
								? workOptions.filter(option => {
										return searchParams.get('works') === option.value
									})
								: [{ label: 'Photos and Videos', value: 'all' }]
						}
						options={workOptions}
						placeholder="types of works"
					/>
					<Input
						{...getInputProps(fields.search, { type: 'search' })}
						type="search"
						name="search"
						autoFocus
						defaultValue={searchParams.get('search') ?? ''}
						placeholder="Search"
						className="mt-0 w-full bg-white text-[0.8rem]"
					/>
					<Button
						onClick={() => setShowFilters(!showFilters)}
						variant={'secondary'}
						className="w-full border border-black bg-[#95AFB7] text-[0.8rem] text-black"
					>
						<Icon name={!showFilters ? 'plus-circled' : 'minus-circled'}>
							{!showFilters ? 'Show Filters' : 'Hide Filters'}
						</Icon>
					</Button>
					{showFilters && (
						<>
							<MySelect
								{...getSelectProps(fields.contributors)}
								name="contributors"
								className="bg-white text-[0.8rem]"
								isMulti
								defaultOptions={artistOptions.filter(option => {
									return searchParams
										.getAll('contributors')
										.includes(option.value)
								})}
								singularItem="contributor"
								onChange={handleSelectChange}
								options={artistOptions}
								placeholder="Contributors"
							/>
							<MySelect
								{...getSelectProps(fields.wildlife)}
								name="wildlife"
								className="bg-white text-[0.8rem]"
								isMulti
								defaultOptions={wildlifeOptions.filter(option => {
									return searchParams.getAll('wildlife').includes(option.value)
								})}
								singularItem="type of wildlife"
								pluralItem="types of wildlife"
								onChange={handleSelectChange}
								options={wildlifeOptions}
								placeholder="Wildlife"
							/>
							<MySelect
								{...getSelectProps(fields.ecosystems)}
								name="ecosystems"
								isMulti
								className="bg-white text-[0.8rem]"
								defaultOptions={ecosystemOptions.filter(option => {
									return searchParams
										.getAll('ecosystems')
										.includes(option.value)
								})}
								singularItem="ecosystem"
								pluralItem="ecosystems"
								onChange={handleSelectChange}
								options={ecosystemOptions}
								placeholder="Ecosystems"
							/>
							<MySelect
								{...getSelectProps(fields.categories)}
								name="categories"
								isMulti
								className="bg-white text-[0.8rem]"
								defaultOptions={categoryOptions.filter(option => {
									return searchParams
										.getAll('categories')
										.includes(option.value)
								})}
								singularItem="category"
								pluralItem="categories"
								onChange={handleSelectChange}
								options={categoryOptions}
								placeholder="Categories"
							/>
							<MySelect
								{...getSelectProps(fields.perspectives)}
								name="perspectives"
								isMulti
								className="bg-white text-[0.8rem]"
								defaultOptions={perspectiveOptions.filter(option => {
									return searchParams
										.getAll('perspectives')
										.includes(option.value)
								})}
								singularItem="perspective"
								pluralItem="perspectives"
								onChange={handleSelectChange}
								options={perspectiveOptions}
								placeholder="Perspectives"
							/>
						</>
					)}
					<StatusButton
						variant={'default'}
						type="submit"
						status={isSubmitting ? 'pending' : 'idle'}
						className="flex w-full items-center justify-center text-[0.8rem]"
					>
						<Icon name="magnifying-glass" size="md" />
						<span className="sr-only">Search</span>
					</StatusButton>
				</Form>
			</Sidebar>
			<Main>
				<Outlet />
			</Main>
		</>
	)
}
